import { EditOutlined, InfoCircleOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Button, Collapse, Layout, PageHeader, Space, Spin, Tooltip, Typography } from 'antd';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Field, FieldGroup, Grid, GridCell, GridRow } from '../../models';
import {
  addSocketHandler,
  apiClient,
  apiErrorMessage,
  generateRandomString,
  getFieldIdsUserCanEditValue,
  hasViewProcessingRecordsPermission,
  parseHyperlink,
  removeSocketHandler,
} from '../../utils';
import { UserContext } from '../contexts';
import { CellEditPage } from './cell-edit-page';
import { GridCellContent } from './grid-cell';
import { FloatingModal } from '../../elements';

export function RowCardPage(props: {
  gridId?: string;
  grid?: Grid;
  rowId?: string;
  onSaved: (row: GridRow) => void;
  onSubGridChanged?: () => void;
}) {
  const { Content } = Layout;
  const { Text } = Typography;

  const [gridLoading, setGridLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState(false);
  const [grid, setGrid] = useState<Grid>();
  const [row, setRow] = useState<GridRow>();
  const rowRef = useRef<GridRow>();
  const [cellDetailsModalOpen, setCellDetailsModalOpen] = useState<any>();
  const [showTasks, setShowTasks] = useState(false);
  const [hasActiveOperation, setHasActiveOperation] = useState(false);
  const [editableFieldIds, setEditableFieldIds] = useState<string[]>();
  const [activeFieldGroups, setActiveFieldGroups] = useState<string[]>([]);

  const { user } = useContext(UserContext);

  const loadGrid = useCallback(() => {
    if ((!props.gridId && !props.grid) || grid) {
      return;
    }

    setGridLoading(true);
    apiClient
      .get(`grids/${props.gridId || props.grid?.id}`)
      .then((response: any) => {
        const responseGrid = response.data as Grid;
        setGrid(responseGrid);

        if (responseGrid?.fieldGroups?.length) {
          const newActiveFieldGroups = responseGrid.fieldGroups.map(fg => fg.id);
          setActiveFieldGroups(newActiveFieldGroups);
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => setGridLoading(false));
  }, [props.grid, props.gridId, grid]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  const loadRow = useCallback(
    (force?: boolean, silent?: boolean) => {
      if (((!props.gridId && !props.grid && !grid) || !props.rowId || row) && !force) {
        return;
      }

      const queryParams = [`gridId=${props.gridId || props.grid?.id || grid?.id}`, `rowId=${props.rowId}`, 'includeTasks=true'];
      const queryParamsStr = queryParams.join('&');

      if (!silent) {
        setRowLoading(true);
      }
      apiClient
        .get(`records/record?${queryParamsStr}`)
        .then((response: any) => {
          setRow(response.data as GridRow);
          rowRef.current = response.data as GridRow;
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        })
        .finally(() => setRowLoading(false));
    },
    [grid, props.grid, props.gridId, props.rowId, row],
  );

  useEffect(() => {
    loadRow();
  }, [loadRow]);

  useEffect(() => {
    const fieldIds = getFieldIdsUserCanEditValue(user, props.gridId || props.grid?.id);
    setEditableFieldIds(fieldIds);
  }, [props.grid?.id, props.gridId, user]);

  useEffect(() => {
    if (!grid) {
      return;
    }

    const key = 'rowCardPage-' + props.rowId + '-' + generateRandomString();

    addSocketHandler('record-updated', key, (data: any) => {
      if (data.recordId === rowRef.current?.id) {
        loadRow(true, true);
      }
    });

    addSocketHandler('cell-updated', key, (data: any) => {
      const newRow = { ...rowRef.current } as GridRow;

      if (data.recordId !== rowRef.current?.id) {
        return;
      }

      if (!Object.keys(newRow.cells)?.includes(data.fieldId)) {
        return;
      }

      if (newRow.cells[data.fieldId].value === data.value) {
        return;
      }

      newRow.cells[data.fieldId].value = data.value;
      newRow.cells[data.fieldId].valueRef = data.valueRef;
      newRow.cells[data.fieldId].errors = data.errors || [];
      setRow(newRow);
      rowRef.current = newRow;
    });

    return () => {
      removeSocketHandler('record-updated', key);
      removeSocketHandler('cell-updated', key);
    };
  }, [grid, loadRow, props.rowId]);

  const openCellDetailsModal = (field: Field, rowId: string) => {
    if (!user?.roles?.some(r => r.name === 'Misafir')) {
      setCellDetailsModalOpen({ field, rowId });
    }
  };

  const closeCellDetailsModal = () => {
    setCellDetailsModalOpen(undefined);
  };

  const onShowTasksClick = () => {
    setShowTasks(!showTasks);
  };

  const onOperationStarted = () => {
    setHasActiveOperation(true);
  };

  const onOperationEnded = () => {
    setHasActiveOperation(false);
  };

  const onSaved = (row: GridRow) => {
    const rowWithKey = { ...row, key: row.id };
    setRow(rowWithKey);
    rowRef.current = rowWithKey;
    props.onSaved(rowWithKey);
  };

  const onSubGridChanged = () => {
    if (props.onSubGridChanged) {
      props.onSubGridChanged();
    }
    loadRow(true);
  };

  const onFieldGroupClick = (fieldGroupId: string | string[]) => {
    let newActiveFieldGroups = [...activeFieldGroups];
    if (newActiveFieldGroups.some(id => id === fieldGroupId)) {
      newActiveFieldGroups = newActiveFieldGroups.filter(id => id !== fieldGroupId);
    } else {
      newActiveFieldGroups.push(fieldGroupId as string);
    }
    setActiveFieldGroups(newActiveFieldGroups);
  };

  const onToggleFieldGroupsClick = () => {
    if (activeFieldGroups?.length) {
      setActiveFieldGroups([]);
    } else {
      const fieldGroupIds = grid?.fieldGroups?.map(fg => fg.id);
      if (fieldGroupIds?.length) {
        setActiveFieldGroups(fieldGroupIds);
      }
    }
  };

  const fieldTr = (field: Field) => {
    return (
      <tr>
        <td
          style={{
            width: '32%',
            border: '1px solid #d9d9d9',
            padding: '0.5rem',
            backgroundColor:
              field.isHighlighted && (editableFieldIds?.includes('*') || editableFieldIds?.includes(field.id)) ? '#fff2f0' : '#fafafa',
            fontWeight: 600,
          }}
        >
          <NameDescription name={field.displayName || field.name} description={field.description} />
        </td>
        <td style={{ width: '68%', border: '1px solid #d9d9d9', padding: '0.5rem', wordBreak: 'break-all' }}>
          <GridCellContent
            grid={grid}
            rowId={row?.id || ''}
            field={field}
            cell={row?.cells ? row?.cells[field.id] || new GridCell() : new GridCell()}
            isObsolete={row?.isObsolete || false}
            isEllipsis={false}
            showTasks={showTasks}
            hasActiveOperation={hasActiveOperation}
            onOperationStarted={onOperationStarted}
            onOperationEnded={onOperationEnded}
            onSaved={props.onSaved}
            onSubGridChanged={onSubGridChanged}
            onCellClick={openCellDetailsModal}
            fromCardDetails={true}
          />
        </td>
      </tr>
    );
  };

  const fieldGroupTr = (fieldGroup: FieldGroup, scheme: any[]) => {
    return (
      <tr>
        <td colSpan={2} style={{ padding: 0, border: 0 }}>
          <Collapse
            activeKey={activeFieldGroups.some(id => id === fieldGroup.id) ? fieldGroup.id : undefined}
            style={{ marginTop: '1rem', marginBottom: '1rem', borderColor: fieldGroup.hexCodeBackground }}
            onChange={() => onFieldGroupClick(fieldGroup.id)}
          >
            <Collapse.Panel
              style={{
                backgroundColor: fieldGroup.hexCodeBackground,
                borderColor: fieldGroup.hexCodeBackground,
              }}
              header={
                <Space>
                  <span style={{ color: fieldGroup.hexCodeTitle, fontWeight: 700 }}>{fieldGroup.name}</span>
                  <Tooltip title={fieldGroup.description}>
                    <Text type="secondary">
                      <InfoCircleOutlined />
                    </Text>
                  </Tooltip>
                </Space>
              }
              key={fieldGroup.id}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {scheme?.map(schemeItem => {
                    if (schemeItem.itemType === 'field') {
                      const itemField = grid?.fields?.find(f => f.id === schemeItem.id);
                      if (itemField) {
                        return fieldTr(itemField);
                      }
                      return null;
                    } else if (schemeItem.itemType === 'fieldGroup') {
                      const itemFieldGroup = grid?.fieldGroups?.find(fg => fg.id === schemeItem.id);
                      if (itemFieldGroup) {
                        return fieldGroupTr(itemFieldGroup, schemeItem?.children);
                      }
                      return null;
                    }
                    return null;
                  })}
                </tbody>
              </table>
            </Collapse.Panel>
          </Collapse>
        </td>
      </tr>
    );
  };

  return (
    <>
      <PageHeader
        title={grid ? grid?.name + ' kartı' : ''}
        style={{ padding: 0 }}
        extra={[
          <>
            {(grid?.fieldGroups?.length || 0) > 0 && (
              <Button key="collapse" onClick={onToggleFieldGroupsClick}>
                Tüm grupları {activeFieldGroups?.length ? 'gizle' : 'göster'}
              </Button>
            )}
          </>,
          <>
            {row && !row.isObsolete && !user?.roles?.some(r => r.name === 'Misafir') && (
              <Button key="tasks" onClick={onShowTasksClick} icon={<EditOutlined />}>
                {showTasks ? 'Düzenlemeyi kapat' : 'Düzenle'}
              </Button>
            )}
          </>,
        ]}
      />

      {((!!row?.valueTaskCount && row?.valueTaskCount > 0) ||
        Object.values(row?.cells || {}).some(c => c.errors?.length) ||
        (grid?.isRevision && !row?.isLatestRevision) ||
        (grid?.isRevision && row?.revisionComment)) && (
        <Space>
          {row?.recordStatus === 'PROCESSING' && !hasViewProcessingRecordsPermission(user, grid?.id) && (
            <Alert
              message={
                <>
                  <LoadingOutlined style={{ marginRight: '1rem' }} />
                  <span>Bu kayıt henüz işleniyor, lütfen daha sonra tekrar deneyin.</span>
                </>
              }
              type="warning"
            />
          )}

          {grid?.isRevision && row && !row?.isLatestRevision && (
            <Alert
              message={
                <>
                  <Text type="warning" style={{ marginRight: '0.5rem' }}>
                    <WarningOutlined />
                  </Text>
                  Daha yeni bir revizyon mevcut.
                </>
              }
              type="warning"
            />
          )}

          {!!row?.valueTaskCount && row?.valueTaskCount > 0 && (
            <Alert
              message={row?.valueTaskCount + ' bekleyen değişiklik mevcut'}
              type="warning"
              style={{ backgroundColor: 'rgb(250, 173, 20, 0.2)', borderBlock: 'rgb(250, 173, 20)' }}
            />
          )}
          {Object.values(row?.cells || {}).some(c => c.errors?.filter(x => x !== 'Onaylanmamış değer').length) && (
            <Alert
              message={
                Object.values(row?.cells || {}).reduce(
                  (acc, c) => acc + (c.errors?.filter(x => x !== 'Onaylanmamış değer').length || 0),
                  0,
                ) + ' kural hatası mevcut'
              }
              type="error"
            />
          )}
        </Space>
      )}
      <Content style={{ padding: 0, marginTop: '1rem' }}>
        <Spin spinning={gridLoading || rowLoading}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {grid?.scheme?.map(schemeItem => {
                if (schemeItem.itemType === 'field') {
                  const itemField = grid?.fields?.find(f => f.id === schemeItem.id);
                  if (itemField) {
                    return fieldTr(itemField);
                  }
                  return null;
                } else if (schemeItem.itemType === 'fieldGroup') {
                  const itemFieldGroup = grid?.fieldGroups?.find(fg => fg.id === schemeItem.id);
                  if (itemFieldGroup) {
                    return fieldGroupTr(itemFieldGroup, schemeItem?.children);
                  }
                  return null;
                }
                return null;
              })}
            </tbody>
          </table>
        </Spin>
      </Content>
      <FloatingModal
        title="Veri detayları"
        visible={cellDetailsModalOpen}
        footer={null}
        onCancel={closeCellDetailsModal}
        destroyOnClose={true}
        width={'90%'}
      >
        <CellEditPage
          record={row}
          grid={props.grid}
          gridId={props.grid?.id}
          field={cellDetailsModalOpen?.field}
          rowId={cellDetailsModalOpen?.rowId}
          allowRecordWithErrors={grid?.allowRecordWithErrors}
          onSaved={onSaved}
        />
      </FloatingModal>
    </>
  );
}

function NameDescription(props: { name?: string; description?: string }) {
  const { Text } = Typography;

  return (
    <Space>
      <Text>{props.name}</Text>
      <Tooltip title={parseHyperlink(props.description)}>
        <Text type="secondary">
          <InfoCircleOutlined />
        </Text>
      </Tooltip>
    </Space>
  );
}
